// Minimal, escape-first markdown renderer for Automate's prose modes
// (Plan / Map / Review). Supports only what those answers actually use:
// headings, bold/italic, inline code, fenced code, bullet and numbered lists.
// Everything is HTML-escaped before any markup is applied, so agent output can
// never inject markup.

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Applies to already-escaped text.
function inline(text) {
  return text
    .replace(/`([^`]+)`/g, '<code class="font-mono text-[14px] bg-[rgba(2,6,23,0.8)] border border-[#1f2937] rounded px-1 py-0.5 text-[#cbd5e1] [overflow-wrap:anywhere]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-[#f1f5f9]">$1</strong>')
    .replace(/(^|[\s(])\*([^*\n]+)\*/g, '$1<em>$2</em>');
}

const H_CLASSES = {
  1: 'text-[19px] font-bold text-[#f8fafc] mt-4 first:mt-0 mb-1.5',
  2: 'text-[17px] font-semibold text-[#f8fafc] mt-4 first:mt-0 mb-1.5',
  3: 'text-[15.5px] font-semibold text-[#e2e8f0] mt-3 first:mt-0 mb-1'
};

export function renderMarkdown(source) {
  const lines = escapeHtml(source || '').replace(/\r\n/g, '\n').split('\n');
  const out = [];

  let listType = null; // 'ul' | 'ol'
  let inFence = false;
  let fence = [];
  let paragraph = [];

  const closeList = () => {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  };

  const flushParagraph = () => {
    if (paragraph.length) {
      out.push(
        `<p class="text-[15.5px] leading-[1.65] text-[#cbd5e1] my-2 [overflow-wrap:anywhere]">${inline(paragraph.join(' '))}</p>`
      );
      paragraph = [];
    }
  };

  const flushAll = () => {
    flushParagraph();
    closeList();
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      if (inFence) {
        out.push(
          `<pre class="my-2.5 border border-[#1f2937] rounded-[10px] bg-[rgba(2,6,23,0.8)] px-3.5 py-3 overflow-x-auto font-mono text-[14px] leading-[1.7] text-[#cbd5e1]">${fence.join('\n')}</pre>`
        );
        fence = [];
        inFence = false;
      } else {
        flushAll();
        inFence = true;
      }
      continue;
    }

    if (inFence) {
      fence.push(line);
      continue;
    }

    if (!line.trim()) {
      flushAll();
      continue;
    }

    // Table: a pipe row followed by a |---|---| separator. Map mode leans on these.
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      flushAll();
      const cells = (row) =>
        row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

      const head = cells(line);
      i += 2; // skip the separator row
      const body = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        body.push(cells(lines[i]));
        i += 1;
      }
      i -= 1; // the for-loop will advance past the last consumed row

      const th = head
        .map(
          (c) =>
            `<th class="text-left font-semibold text-[#e2e8f0] px-2.5 py-1.5 border-b border-[#1f2937]">${inline(c)}</th>`
        )
        .join('');
      const tr = body
        .map(
          (row) =>
            `<tr>${row
              .map(
                (c) =>
                  `<td class="align-top px-2.5 py-1.5 border-b border-[#111827] text-[#cbd5e1]">${inline(c)}</td>`
              )
              .join('')}</tr>`
        )
        .join('');

      out.push(
        `<div class="my-2.5 overflow-x-auto"><table class="w-full border-collapse text-[14.5px] leading-[1.55]"><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></div>`
      );
      continue;
    }

    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushAll();
      out.push('<hr class="my-3 border-0 border-t border-[#1f2937]">');
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushAll();
      const level = heading[1].length;
      out.push(`<div class="${H_CLASSES[level]}">${inline(heading[2].trim())}</div>`);
      continue;
    }

    // Nested bullets keep their indent — the call map in Map mode relies on it.
    const bullet = line.match(/^(\s*)[-*+]\s+(.*)$/);
    if (bullet) {
      flushParagraph();
      if (listType !== 'ul') {
        closeList();
        out.push('<ul class="list-disc pl-5 my-1.5 space-y-1">');
        listType = 'ul';
      }
      const indent = Math.min(Math.floor(bullet[1].length / 2), 4);
      const pad = indent ? ` style="margin-left:${indent * 1.1}rem"` : '';
      out.push(`<li class="text-[15.5px] leading-[1.6] text-[#cbd5e1] [overflow-wrap:anywhere]"${pad}>${inline(bullet[2])}</li>`);
      continue;
    }

    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (numbered) {
      flushParagraph();
      if (listType !== 'ol') {
        closeList();
        out.push('<ol class="list-decimal pl-5 my-1.5 space-y-1">');
        listType = 'ol';
      }
      out.push(`<li class="text-[15.5px] leading-[1.6] text-[#cbd5e1] [overflow-wrap:anywhere]">${inline(numbered[1])}</li>`);
      continue;
    }

    closeList();
    paragraph.push(line.trim());
  }

  if (inFence && fence.length) {
    out.push(
      `<pre class="my-2.5 border border-[#1f2937] rounded-[10px] bg-[rgba(2,6,23,0.8)] px-3.5 py-3 overflow-x-auto font-mono text-[14px] leading-[1.7] text-[#cbd5e1]">${fence.join('\n')}</pre>`
    );
  }
  flushAll();

  return out.join('\n');
}
